<template>
  <header class="site-nav">
    <div class="edge-rack" aria-hidden="true">
      <div class="edge-rack-teeth" :style="rackStyle">
        <span v-for="tooth in rackToothCount" :key="tooth" class="edge-rack-tooth"></span>
      </div>
    </div>

    <div class="shell nav-inner">
      <router-link class="brand" to="/" aria-label="Centerfire Services home" :style="brandStyle">
        <span class="brand-rotor">
          <img :src="brand.logo" alt="Centerfire Services" />
        </span>
      </router-link>

      <div class="nav-actions">
        <a class="nav-phone" :href="phoneHref">
          <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
            <path
              d="M6.6 10.8a15.1 15.1 0 0 0 6.6 6.6l2.2-2.2a1 1 0 0 1 1-.24 11.4 11.4 0 0 0 3.6.58 1 1 0 0 1 1 1V20a1 1 0 0 1-1 1A17 17 0 0 1 3 4a1 1 0 0 1 1-1h3.5a1 1 0 0 1 1 1 11.4 11.4 0 0 0 .57 3.6 1 1 0 0 1-.25 1z"
              fill="currentColor"
            />
          </svg>
          <span>{{ brand.phone }}</span>
        </a>

        <router-link class="btn btn-primary cta" :to="primaryAction.to">{{ primaryAction.label }}</router-link>
      </div>

      <button
        class="mobile-toggle"
        @click="open = !open"
        :aria-expanded="String(open)"
        aria-controls="primary-nav"
        aria-label="Toggle menu"
        type="button"
      >
        <span></span>
        <span></span>
        <span></span>
      </button>

      <nav id="primary-nav" class="links" :class="{ open }">
        <router-link to="/" @click="open = false">Home</router-link>
        <router-link to="/maintunits" @click="open = false">Maintained Units</router-link>
        <router-link to="/forsale" @click="open = false">Center FireSale</router-link>
        <router-link to="/blog" @click="open = false">Blog</router-link>
      </nav>
    </div>
  </header>
</template>

<script>
import { brand } from '@/data/content';

/* The mark's central gear carries this many teeth, so one tooth of rack travel
   is a sixteenth of a turn. At the artwork's reference width the rack's tooth
   pitch measures 23px, which is where NOMINAL_PITCH comes from — but it is only
   the fallback, because the rendered pitch is measured (see measureRackPitch). */
const GEAR_TEETH = 16;
const REFERENCE_BRAND_W = 160;
const NOMINAL_PITCH = 23;

export default {
  data() {
    return {
      open: false,
      brand,
      frame: 0,
      scrollY: 0,
      reduceMotion: false,
      rackToothCount: 80,
      logoScale: 1,
      rackPitch: NOMINAL_PITCH,
      // Pixels of rack travel per pixel of scroll. Replaced on mount, once the
      // rack has been measured and the page's length is known.
      motionRate: 0.42,
    };
  },
  computed: {
    primaryAction() {
      return { to: '/services-request', label: this.brand.scheduleText };
    },
    phoneHref() {
      return `tel:${this.brand.phone.replace(/\D/g, '')}`;
    },
    rackTravel() {
      return this.reduceMotion ? 0 : this.scrollY * this.motionRate;
    },
    brandStyle() {
      // A tooth of rack under the mark is a tooth of rotation out of sixteen.
      // Both sides read the same measured pitch, so neither can outrun the other.
      const rotation = (this.rackTravel / this.rackPitch) * (360 / GEAR_TEETH);
      return { '--logo-rotation': `${rotation}deg` };
    },
    rackStyle() {
      // Keep one continuous rack in motion. Wrapping this value every few
      // teeth creates a visible phase jump at fractional responsive sizes.
      return { transform: `translate3d(0, ${-this.rackTravel}px, 0)` };
    },
  },
  mounted() {
    this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.updateScroll();
    window.addEventListener('scroll', this.requestScrollUpdate, { passive: true });
    window.addEventListener('resize', this.requestScrollUpdate, { passive: true });
  },
  beforeUnmount() {
    window.removeEventListener('scroll', this.requestScrollUpdate);
    window.removeEventListener('resize', this.requestScrollUpdate);
    window.cancelAnimationFrame(this.frame);
  },
  watch: {
    $route() {
      this.open = false;
    },
  },
  methods: {
    requestScrollUpdate() {
      if (this.frame) return;
      this.frame = window.requestAnimationFrame(() => {
        this.frame = 0;
        this.updateScroll();
      });
    },
    /* Measure the rack rather than recompute it. Every tooth and gap is a
       fraction of --brand-w and the browser snaps each one to the device pixel
       grid, so at the phone's 52px mark the rendered pitch is 7.469px where the
       arithmetic says 7.475. Per tooth that is invisible; over the hundred-odd
       teeth a page of scroll turns, it walks the mark out of mesh. */
    measureRackPitch() {
      const teeth = this.$el.querySelectorAll('.edge-rack-tooth');
      if (teeth.length < 2) return NOMINAL_PITCH * this.logoScale;
      const pitch = teeth[1].getBoundingClientRect().top - teeth[0].getBoundingClientRect().top;
      return pitch > 0 ? pitch : NOMINAL_PITCH * this.logoScale;
    },
    updateScroll() {
      this.scrollY = window.scrollY || 0;
      const rotor = this.$el.querySelector('.brand-rotor');
      // offsetWidth is the untransformed layout width. A rotated rectangular
      // logo has a changing bounding-box width, which must not alter rack speed.
      this.logoScale = rotor ? rotor.offsetWidth / REFERENCE_BRAND_W : 1;
      this.rackPitch = this.measureRackPitch();
      const pageRange = Math.max(1, document.documentElement.scrollHeight - window.innerHeight);
      // End the page on a complete tooth cycle. The previous fixed rate left
      // the bottom at 57.39 cycles, which looked like accumulated phase drift.
      // Whole cycles of the measured pitch, so the bottom of the page lands on
      // a tooth at every breakpoint rather than only where the pitch is exact.
      const completeCycles = Math.max(1, Math.round((pageRange * 0.42 * this.logoScale) / this.rackPitch));
      this.motionRate = (completeCycles * this.rackPitch) / pageRange;
      const maximumTravel = pageRange * this.motionRate;
      document.documentElement.style.setProperty('--rack-travel-y', `${-this.rackTravel}px`);
      const requiredRackHeight = window.innerHeight + maximumTravel + 120 * this.logoScale;
      this.rackToothCount = Math.max(80, Math.ceil(requiredRackHeight / this.rackPitch) + 2);
    },
  },
};
</script>

<style scoped>
.site-nav {
  position: sticky;
  top: 0;
  z-index: 30;
  background: var(--black);

  --nav-h: 116px;
  --brand-w: clamp(124px, 18.1vw, 160px);
  /* The farthest corner of the 594x640 artwork sweeps a 1.4825:1 circle at
     this rendered width. Eight pixels of clearance surround that full sweep. */
  --logo-envelope: calc(var(--brand-w) * 1.4825 + 16px);
  --logo-inset: calc(var(--brand-w) * 0.243 + 8px);
  --plate-drop: calc(var(--logo-envelope) - var(--nav-h));
  --plate-w: var(--logo-envelope);

  /* The box the mark is vertically centred in. On desktop it is the whole
     envelope, so the gear hangs down into the stepped plate; the mobile
     breakpoint drops that plate and shrinks this to the bar. Anything that has
     to stay in mesh with the gear is derived from it rather than restated per
     breakpoint, because only one of the two ever gets remembered. */
  --brand-box-h: var(--logo-envelope);
  /* The gear's centre is not the rotor box's centre: the artwork puts it 0.625%
     of the rotor's height lower (see .brand-rotor's transform-origin), and the
     594x640 rotor is 640/594 as tall as it is wide. */
  --gear-centre-y: calc(var(--brand-box-h) / 2 + var(--brand-w) * 0.006734);
}

.edge-rack {
  position: fixed;
  top: 0;
  left: var(--logo-inset);
  z-index: 4;
  width: calc(var(--brand-w) * 0.15);
  height: var(--logo-envelope);
  overflow: hidden;
  pointer-events: none;
  opacity: 0.8;
  filter: drop-shadow(0 0 10px rgba(255, 90, 2, 0.2));
}

.edge-rack::before {
  content: '';
  position: absolute;
  inset: 0 auto 0 0;
  /* Fill the rack to the logo gear's outside diameter. The remaining exposed
     tooth is 7px at full size, matching the round gear's measured tooth depth. */
  width: calc(var(--brand-w) * 0.10625);
  background: var(--orange);
}

.edge-rack-teeth {
  position: absolute;
  left: calc(var(--brand-w) * 0.01875);
  /* Put the gear's leftmost tooth in the middle of a rack gap: that gap sits
     8.7825 tooth pitches (of the 0.14375-of-the-width pitch below) above the
     gear's centre. Phasing off the centre holds the mesh at any --brand-box-h.
     A constant offset measured off the lowered desktop logo does not: it left
     the mobile bar a third of a tooth out, crest riding on crest. */
  top: calc(var(--gear-centre-y) - var(--brand-w) * 1.262484);
  width: calc(var(--brand-w) * 0.13125);
  display: flex;
  flex-direction: column;
  gap: calc(var(--brand-w) * 0.0625);
  will-change: transform;
}

.edge-rack-tooth {
  width: calc(var(--brand-w) * 0.13125);
  height: calc(var(--brand-w) * 0.08125);
  flex: 0 0 calc(var(--brand-w) * 0.08125);
  background: var(--orange);
  clip-path: polygon(0 0, 72% 0, 100% 20%, 100% 80%, 72% 100%, 0 100%);
}

/* The rule runs at the nav's baseline, but only to the right of the logo plate. */
.site-nav::after {
  content: '';
  position: absolute;
  left: var(--plate-w);
  right: 0;
  bottom: 0;
  height: 1px;
  background: var(--orange);
}

/* ...where it steps down and heads back to the left edge, boxing in the bottom
   of the logo. The right border is the drop; the bottom border is the run. */
.site-nav::before {
  content: '';
  position: absolute;
  left: 0;
  top: 100%;
  width: var(--plate-w);
  height: var(--plate-drop);
  border-right: 1px solid var(--orange);
  border-bottom: 1px solid var(--orange);
  background: var(--black);
  /* Above the mobile menu panel, so the plate overlaps it rather than leaving a
     strip of hero showing between the rule and the panel. */
  z-index: 3;
}

.nav-inner {
  min-height: var(--nav-h);
  display: grid;
  /* Column 1 is empty — it reserves room for the absolutely positioned brand. */
  grid-template-columns: 178px 1fr auto;
  align-items: center;
  gap: 1.2rem;
}

/* Every bar item is pinned to row 1 explicitly. Grid auto-placement only moves
   forward, so a later item asking for an earlier column drops to a new row —
   which silently made this a two-row nav the moment the actions block was
   authored ahead of the links in the markup. */
.links {
  grid-column: 2;
  grid-row: 1;
}

.nav-actions {
  grid-column: 3;
  grid-row: 1;
  display: flex;
  align-items: center;
  gap: 1rem;
}

/* The number is the conversion for a mobile mechanic, so it is a real tel:
   link and it survives the breakpoint that hides the Schedule button — on a
   phone it is the single most useful control in the bar. */
.nav-phone {
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  color: var(--white);
  font-weight: 600;
  font-size: 0.95rem;
  letter-spacing: 0.01em;
  white-space: nowrap;
  transition: color 0.25s ease;
}

.nav-phone svg {
  width: 1.05em;
  height: 1.05em;
  flex: 0 0 auto;
  color: var(--orange);
}

.nav-phone:hover,
.nav-phone:focus-visible {
  color: var(--orange);
}

/* Lifted out of the grid so the mark can hang into the stepped-down box. */
.brand {
  position: absolute;
  left: var(--logo-inset);
  top: 0;
  height: var(--brand-box-h);
  display: flex;
  align-items: center;
  z-index: 5;
}

.brand-rotor {
  display: block;
  width: var(--brand-w);
  transform: rotate(var(--logo-rotation));
  /* Measured from the actual circular gear, not the transparent image box. */
  transform-origin: 49.83% 50.625%;
  transition: filter 0.35s ease;
  will-change: transform;
}

.brand:hover .brand-rotor,
.brand:focus-visible .brand-rotor {
  filter: drop-shadow(0 0 13px rgba(255, 90, 2, 0.46));
}

.brand img {
  width: 100%;
}

.links {
  display: flex;
  gap: 1.2rem;
  justify-content: center;
}

.links a {
  color: var(--white);
  padding: 0.45rem 0.2rem;
  font-weight: 600;
  border-bottom: 2px solid transparent;
}

.links a:hover {
  color: var(--orange);
}

.links a.router-link-active {
  color: var(--orange);
  border-bottom-color: var(--orange);
}

.admin-menu-link {
  display: none;
}

.cta {
  font-size: 0.9rem;
}

.mobile-toggle {
  display: none;
  flex-direction: column;
  gap: 5px;
  background: none;
  border: 0;
  padding: 0.4rem;
}

.mobile-toggle span {
  width: 24px;
  height: 2px;
  background: var(--orange);
}

@media (max-width: 1200px) {
  .site-nav {
    --nav-h: 98px;
    /* Keep the complete sweep of the rotating artwork inside the bar. At 52px,
       the logo's measured corner-to-corner envelope is narrower than 98px. */
    --brand-w: 52px;
    --logo-inset: 4vw;
    /* No stepped plate below the bar here, so the mark centres in the bar
       alone. The rack's tooth phase follows this through --gear-centre-y. */
    --brand-box-h: var(--nav-h);
  }

  /* The stepped plate deliberately hangs below the desktop bar. On a phone
     that space belongs to the open navigation, so remove only the plate while
     keeping the scaled gear rail and rotating mark inside the header. */
  .site-nav::before {
    display: none;
  }

  .site-nav::after {
    left: 0;
  }

  .nav-inner {
    grid-template-columns: 1fr auto auto;
  }

  .nav-actions {
    grid-column: 2;
    grid-row: 1;
  }

  .mobile-toggle {
    display: flex;
    grid-column: 3;
    grid-row: 1;
    justify-self: end;
  }

  .links {
    display: none;
    position: absolute;
    z-index: 1;
    top: var(--nav-h);
    left: 0;
    right: 0;
    padding: 1rem 4vw;
    background: var(--black);
    border-bottom: 1px solid var(--orange);
    flex-direction: column;
    gap: 0.9rem;
  }

  .links.open {
    display: flex;
  }

  .admin-menu-link {
    display: block;
  }

  /* The Schedule button goes, the number stays. */
  .cta {
    display: none;
  }

  .nav-phone {
    font-size: 0.88rem;
  }
}

/* Narrowest phones: the logo plate and the toggle leave too little room for
   fourteen characters of number, so it falls back to the handset alone. The
   link keeps an accessible name because the visible text is only hidden. */
@media (max-width: 420px) {
  .nav-phone span {
    position: absolute;
    width: 1px;
    height: 1px;
    overflow: hidden;
    clip-path: inset(50%);
    white-space: nowrap;
  }

  .nav-phone svg {
    width: 1.5rem;
    height: 1.5rem;
  }
}

@media (prefers-reduced-motion: reduce) {
  .brand-rotor,
  .edge-rack-teeth {
    transform: none !important;
  }
}
</style>
